/** Closed catalog of REEL&SHORTS hook-title templates — mirrors backend/reels_shorts_title.py. */

export const TITLE_CANVAS_H = 1920;
export const TITLE_W = 1080;
export const TITLE_H = 360;
export const TITLE_TOP = 36;
export const TITLE_CLEAR_GAP = 24;
export const TITLE_STACK_VISUAL_H = 200;
export const TITLE_GRADIENT_BORDER =
  'linear-gradient(135deg, #fde047 0%, #ffffff 32%, #e8ff47 68%, #facc15 100%)';

export const TITLE_IMPACT_STACK = 'impact_stack';
export const TITLE_YELLOW_BANNER = 'yellow_banner';
export const TITLE_NUMBER_HOOK = 'number_hook';
export const TITLE_MARKER_HIGHLIGHT = 'marker_highlight';
export const TITLE_ALERT_KICKER = 'alert_kicker';
export const TITLE_BLACK_CAPSULE = 'black_capsule';

export const TITLE_FILL_TEMPLATES = [
  { id: 'lime', label: 'Lime', color: '#e8ff47', color2: '#facc15', ink: '#0c0e12' },
  { id: 'gold', label: 'Gold', color: '#fde047', color2: '#ffffff', ink: '#0c0e12' },
  { id: 'sunset', label: 'Sunset', color: '#fb7185', color2: '#f97316', ink: '#ffffff' },
  { id: 'ocean', label: 'Ocean', color: '#22d3ee', color2: '#6366f1', ink: '#ffffff' },
  { id: 'berry', label: 'Berry', color: '#e879f9', color2: '#f43f5e', ink: '#ffffff' },
  { id: 'mono', label: 'Mono', color: '#0c0e12', color2: '#334155', ink: '#ffffff' },
  { id: 'custom', label: 'Custom', color: '#e8ff47', color2: '#facc15', ink: '#0c0e12' },
];

export const TITLE_FONT_FAMILIES = [
  { id: 'condensed', label: 'Condensed', css: '"Arial Narrow", "Helvetica Neue Condensed", Arial, sans-serif', weight: 800 },
  { id: 'black', label: 'Black', css: '"Arial Black", Impact, Arial, sans-serif', weight: 900 },
  { id: 'rounded', label: 'Rounded', css: '"Nunito", "Arial Rounded MT Bold", Arial, sans-serif', weight: 800 },
  { id: 'serif', label: 'Serif', css: 'Georgia, "Times New Roman", serif', weight: 800 },
];

const FILL_IDS = new Set(TITLE_FILL_TEMPLATES.map((row) => row.id));
const FONT_IDS = new Set(TITLE_FONT_FAMILIES.map((row) => row.id));
const HEX_COLOR = /^#?[0-9a-fA-F]{6}$/;

export const REELS_SHORTS_TITLE_TEMPLATES = [
  {
    id: TITLE_IMPACT_STACK,
    label: 'Impact stack',
    description: 'Two stacked bars — white hook on black, yellow punch line.',
  },
  {
    id: TITLE_YELLOW_BANNER,
    label: 'Yellow banner',
    description: 'Rounded banner that hugs the title with roomy padding.',
  },
  {
    id: TITLE_NUMBER_HOOK,
    label: 'Number hook',
    description: 'Giant list number plus a two-line headline.',
  },
  {
    id: TITLE_MARKER_HIGHLIGHT,
    label: 'Marker highlight',
    description: 'White headline with a yellow highlighter behind the second line.',
  },
  {
    id: TITLE_ALERT_KICKER,
    label: 'Alert kicker',
    description: 'Small red kicker chip over a black headline box.',
  },
  {
    id: TITLE_BLACK_CAPSULE,
    label: 'Black capsule',
    description: 'One thick rounded box with two-tone white and yellow type.',
  },
];

const TEMPLATE_IDS = new Set(REELS_SHORTS_TITLE_TEMPLATES.map((row) => row.id));

const ALIASES = {
  stack: TITLE_IMPACT_STACK,
  instagram: TITLE_IMPACT_STACK,
  viral: TITLE_IMPACT_STACK,
  banner: TITLE_YELLOW_BANNER,
  number: TITLE_NUMBER_HOOK,
  highlight: TITLE_MARKER_HIGHLIGHT,
  marker: TITLE_MARKER_HIGHLIGHT,
  alert: TITLE_ALERT_KICKER,
  news: TITLE_ALERT_KICKER,
  capsule: TITLE_BLACK_CAPSULE,
};

export function titleTemplateMeta(id) {
  return REELS_SHORTS_TITLE_TEMPLATES.find((row) => row.id === id) || null;
}

export function normalizeTitleTemplateId(value) {
  const raw = String(value || '').trim().toLowerCase().replace(/[-\s]+/g, '_');
  const mapped = ALIASES[raw] || raw;
  return TEMPLATE_IDS.has(mapped) ? mapped : null;
}

export function splitTitleLines(title) {
  const raw = String(title || '').replace(/\s+/g, ' ').trim();
  if (!raw) return { line1: '', line2: '' };
  const seps = [' — ', ' – ', ' - ', ': ', ' / ', ' | '];
  for (const sep of seps) {
    const idx = raw.indexOf(sep);
    if (idx >= 0) {
      return {
        line1: raw.slice(0, idx).trim().slice(0, 80),
        line2: raw.slice(idx + sep.length).trim().slice(0, 80),
      };
    }
  }
  const words = raw.split(' ');
  if (words.length <= 4) return { line1: raw.slice(0, 80), line2: '' };
  const mid = Math.max(2, Math.min(5, Math.floor(words.length / 2)));
  return {
    line1: words.slice(0, mid).join(' ').slice(0, 80),
    line2: words.slice(mid).join(' ').slice(0, 80),
  };
}

function normalizePlacement(raw, fallback = 'top') {
  let value = String(raw || '').trim().toLowerCase().replace(/-/g, '_');
  if (value === 'middle') value = 'center';
  if (value === 'top' || value === 'center' || value === 'bottom') return value;
  return fallback;
}

export function layerForPlacement(placement) {
  if (placement === 'bottom') {
    return { x: 0.5, y: 0.82, width: 0.92, height: 0.2, rotation: 0 };
  }
  if (placement === 'center') {
    return { x: 0.5, y: 0.42, width: 0.92, height: 0.2, rotation: 0 };
  }
  return { x: 0.5, y: 0.12, width: 0.92, height: 0.2, rotation: 0 };
}

/** Map a free-drag layer center to the nearest top / center / bottom preset. */
export function placementNearestFromLayer(layer) {
  const y = Number(layer?.y);
  if (!Number.isFinite(y)) return 'top';
  const presets = [
    { id: 'top', y: 0.12 },
    { id: 'center', y: 0.42 },
    { id: 'bottom', y: 0.82 },
  ];
  let best = presets[0];
  let bestDist = Math.abs(y - best.y);
  for (const row of presets.slice(1)) {
    const dist = Math.abs(y - row.y);
    if (dist < bestDist) {
      best = row;
      bestDist = dist;
    }
  }
  return best.id;
}

export function captionLayerForPlacement(placement) {
  if (placement === 'top') {
    return { x: 0.5, y: 0.18, width: 0.88, height: 0.1, rotation: 0 };
  }
  if (placement === 'center') {
    return { x: 0.5, y: 0.5, width: 0.88, height: 0.1, rotation: 0 };
  }
  return { x: 0.5, y: 0.78, width: 0.88, height: 0.1, rotation: 0 };
}

export function sanitizeTitleOverlay(raw, fallbackTitle = '') {
  const data = raw && typeof raw === 'object' ? raw : {};
  let line1 = String(data.line1 || data.line_1 || '').trim().slice(0, 80);
  let line2 = String(data.line2 || data.line_2 || '').trim().slice(0, 80);
  if (!line1 && !line2) {
    const split = splitTitleLines(fallbackTitle);
    line1 = split.line1;
    line2 = split.line2;
  }
  const templateId = normalizeTitleTemplateId(data.template_id || data.template) || TITLE_IMPACT_STACK;
  let enabled = Boolean(data.enabled);
  if (!line1 && !line2) enabled = false;
  const placement = normalizePlacement(data.placement || data.position, 'top');
  const captionPlacement = normalizePlacement(
    data.caption_placement || data.subtitle_placement,
    'bottom',
  );
  let duration = Number(data.duration_seconds);
  if (!Number.isFinite(duration) || duration <= 0) duration = 0;
  else duration = Math.max(1, Math.min(30, duration));

  let layer = null;
  if (data.layer && typeof data.layer === 'object') {
    const x = Number(data.layer.x);
    const y = Number(data.layer.y);
    const width = Number(data.layer.width ?? data.layer.w);
    const height = Number(data.layer.height ?? data.layer.h);
    if ([x, y, width, height].every(Number.isFinite) && width >= 0.04 && height >= 0.04) {
      const rotation = Number(data.layer.rotation);
      layer = {
        x: Math.min(1, Math.max(0, x)),
        y: Math.min(1, Math.max(0, y)),
        width: Math.min(1, Math.max(0.04, width)),
        height: Math.min(1, Math.max(0.04, height)),
        rotation: Number.isFinite(rotation) ? Math.min(180, Math.max(-180, rotation)) : 0,
      };
    }
  }
  if (!layer) layer = layerForPlacement(placement);

  return {
    enabled,
    template_id: templateId,
    line1,
    line2,
    style: sanitizeTitleStyle(data.style || data),
    layer,
    placement,
    duration_seconds: duration,
    caption_placement: captionPlacement,
  };
}

export function hexColor(value, fallback = '#e8ff47') {
  const raw = String(value || '').trim();
  if (HEX_COLOR.test(raw)) return raw.startsWith('#') ? raw : `#${raw}`;
  return fallback;
}

function fillPreset(id) {
  return TITLE_FILL_TEMPLATES.find((row) => row.id === id) || TITLE_FILL_TEMPLATES[0];
}

export function sanitizeTitleStyle(raw) {
  const data = raw && typeof raw === 'object' ? raw : {};
  let family = String(data.font_family || data.family || 'condensed').trim().toLowerCase();
  if (!FONT_IDS.has(family)) family = 'condensed';
  let fontSize = Number(data.font_size ?? data.size ?? 1);
  if (!Number.isFinite(fontSize)) fontSize = 1;
  fontSize = Math.max(0.7, Math.min(1.5, Math.round(fontSize * 100) / 100));
  const uppercase = data.uppercase == null ? true : Boolean(data.uppercase);
  let fillId = String(data.fill_template || data.fill || 'lime').trim().toLowerCase();
  if (!FILL_IDS.has(fillId)) fillId = 'lime';
  const preset = fillPreset(fillId === 'custom' ? 'lime' : fillId);
  let fillColor = hexColor(data.fill_color || data.color, preset.color);
  let fillColor2 = hexColor(data.fill_color2 || data.color2, preset.color2);
  let textColor = hexColor(data.text_color || data.ink, preset.ink);
  if (fillId !== 'custom' && data.fill_color == null && data.color == null) {
    fillColor = preset.color;
    fillColor2 = preset.color2;
    textColor = preset.ink;
  }
  let padX = Number.parseInt(data.pad_x, 10);
  let padY = Number.parseInt(data.pad_y, 10);
  if (!Number.isFinite(padX)) padX = 52;
  if (!Number.isFinite(padY)) padY = 26;
  return {
    font_family: family,
    font_size: fontSize,
    uppercase,
    fill_template: fillId,
    fill_color: fillColor,
    fill_color2: fillColor2,
    text_color: textColor,
    pad_x: Math.max(24, Math.min(96, padX)),
    pad_y: Math.max(12, Math.min(56, padY)),
  };
}

export function titleFillCss(style) {
  const next = sanitizeTitleStyle(style);
  return `linear-gradient(135deg, ${next.fill_color} 0%, ${next.fill_color2} 100%)`;
}

export function titleFontMeta(style) {
  const next = sanitizeTitleStyle(style);
  return TITLE_FONT_FAMILIES.find((row) => row.id === next.font_family) || TITLE_FONT_FAMILIES[0];
}

export function titleTypeCss(style, { fontSize = 12 } = {}) {
  const next = sanitizeTitleStyle(style);
  const family = titleFontMeta(next);
  return {
    fontFamily: family.css,
    fontWeight: family.weight,
    fontSize: `${Math.round(fontSize * next.font_size)}px`,
    color: next.text_color,
    textTransform: next.uppercase ? 'uppercase' : 'none',
    letterSpacing: '0.04em',
    padding: `${Math.round(next.pad_y * 0.38)}px ${Math.round(next.pad_x * 0.38)}px`,
  };
}

export function displayTitleText(text, style) {
  const raw = String(text || '').trim();
  if (!raw) return '';
  return sanitizeTitleStyle(style).uppercase ? raw.toUpperCase() : raw;
}

export function titleVisualHeight(templateId) {
  const id = normalizeTitleTemplateId(templateId) || TITLE_IMPACT_STACK;
  if (id === TITLE_ALERT_KICKER || id === TITLE_BLACK_CAPSULE) {
    return TITLE_H;
  }
  return TITLE_STACK_VISUAL_H;
}

export function titleClearRatio(overlay, fallbackTitle = '', canvasH = TITLE_CANVAS_H) {
  const settings = overlay && typeof overlay === 'object' && 'enabled' in overlay && 'template_id' in overlay
    ? overlay
    : sanitizeTitleOverlay(overlay, fallbackTitle);
  if (!settings.enabled) return 0;
  const height = Number(canvasH) > 0 ? Number(canvasH) : TITLE_CANVAS_H;
  return (TITLE_TOP + titleVisualHeight(settings.template_id) + TITLE_CLEAR_GAP) / height;
}

export function titleLayerForOverlay(overlay, canvasH = TITLE_CANVAS_H) {
  const settings = overlay && typeof overlay === 'object' && 'placement' in overlay
    ? overlay
    : sanitizeTitleOverlay(overlay);
  if (settings.layer && typeof settings.layer === 'object') {
    return { ...settings.layer };
  }
  const placement = normalizePlacement(settings.placement, 'top');
  if (placement === 'bottom' || placement === 'center') {
    return layerForPlacement(placement);
  }
  const clear = titleClearRatio(settings, '', canvasH);
  const height = Math.max(0.08, clear || ((TITLE_TOP + TITLE_STACK_VISUAL_H + TITLE_CLEAR_GAP) / TITLE_CANVAS_H));
  return { x: 0.5, y: height / 2, width: 0.92, height };
}
